/**
 * MultiSortingSelector Component
 *
 * Two-section dropdown with per-row direction toggle for multi-level sorting.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Search, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import clsx from 'clsx';
import type { SortingState, SortByField, SortOrder } from '@/types';
import { useClickOutside } from '@/hooks/useClickOutside';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import { DropdownShell } from './DropdownShell';

export interface MultiSortingSelectorProps {
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  availableOptions: Array<{ value: SortByField; label: string; icon?: ReactNode }>;
  icon: ReactNode;
  compact?: boolean;
}

export function MultiSortingSelector({
  sorting,
  onSortingChange,
  availableOptions,
  icon,
  compact = false,
}: MultiSortingSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const fields = availableOptions.filter((o) => o.value !== 'none');

  useClickOutside(
    [containerRef, dropdownRef],
    () => {
      setOpen(false);
      setQuery('');
      setDropdownPosition(null);
    },
    open
  );

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 280);
      const pos = clampToViewport(rect.left, rect.bottom + 6, width, 300);
      setDropdownPosition({ ...pos, width });
      setSecondaryExpanded(sorting.secondary.field !== 'none');
    } else {
      setDropdownPosition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Expand secondary when it gets a value
  useEffect(() => {
    if (sorting.secondary.field !== 'none') {
      setSecondaryExpanded(true);
    }
  }, [sorting.secondary.field]);

  const hasPrimary = sorting.primary.field !== 'none';
  const hasSecondary = sorting.secondary.field !== 'none';

  const getDirectionLabel = (field: SortByField, order: SortOrder) => {
    const opt = fields.find((f) => f.value === field);
    if (!opt) return '';
    return `${opt.label} ${order === 'asc' ? '\u2191' : '\u2193'}`;
  };

  const triggerLabel = !hasPrimary
    ? 'Nessuno'
    : hasSecondary
      ? `${getDirectionLabel(sorting.primary.field, sorting.primary.order)}, ${getDirectionLabel(sorting.secondary.field, sorting.secondary.order)}`
      : getDirectionLabel(sorting.primary.field, sorting.primary.order);

  const handleClearAll = () => {
    onSortingChange({
      ...sorting,
      primary: { field: 'none', order: 'asc' },
      secondary: { field: 'none', order: 'asc' },
    });
  };

  const handlePrimaryClick = (field: SortByField) => {
    if (sorting.primary.field === field) {
      // Deselect: clear both
      onSortingChange({
        ...sorting,
        primary: { field: 'none', order: 'asc' },
        secondary: { field: 'none', order: 'asc' },
      });
    } else if (sorting.secondary.field === field) {
      // Promote secondary to primary, clear secondary
      onSortingChange({
        ...sorting,
        primary: { field, order: sorting.secondary.order },
        secondary: { field: 'none', order: 'asc' },
      });
    } else {
      onSortingChange({
        ...sorting,
        primary: { field, order: sorting.primary.field === 'none' ? 'asc' : sorting.primary.order },
      });
    }
  };

  const handlePrimaryDirection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSortingChange({
      ...sorting,
      primary: {
        ...sorting.primary,
        order: sorting.primary.order === 'asc' ? 'desc' : 'asc',
      },
    });
  };

  const handleSecondaryClick = (field: SortByField) => {
    if (sorting.secondary.field === field) {
      onSortingChange({
        ...sorting,
        secondary: { field: 'none', order: 'asc' },
      });
    } else {
      onSortingChange({
        ...sorting,
        secondary: { field, order: 'asc' },
      });
    }
  };

  const handleSecondaryDirection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSortingChange({
      ...sorting,
      secondary: {
        ...sorting.secondary,
        order: sorting.secondary.order === 'asc' ? 'desc' : 'asc',
      },
    });
  };

  const handleClearPrimary = () => {
    onSortingChange({
      ...sorting,
      primary: { field: 'none', order: 'asc' },
      secondary: { field: 'none', order: 'asc' },
    });
  };

  const handleClearSecondary = () => {
    onSortingChange({
      ...sorting,
      secondary: { field: 'none', order: 'asc' },
    });
  };

  const filteredFields = query
    ? fields.filter((f) => f.label.toLowerCase().includes(query.toLowerCase()))
    : fields;

  const secondaryFields = filteredFields.filter((f) => f.value !== sorting.primary.field);

  const renderDirectionIcon = (isActive: boolean, order: SortOrder, onClick: (e: React.MouseEvent) => void) => {
    if (!isActive) {
      return (
        <span className="w-5 h-5 flex items-center justify-center text-text-quaternary">
          <ArrowUpDown className="w-3.5 h-3.5" />
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-5 h-5 flex items-center justify-center text-accent-violet hover:bg-accent-violet/10 rounded transition-colors"
        title={order === 'asc' ? 'Crescente (click per decrescente)' : 'Decrescente (click per crescente)'}
      >
        {order === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
      </button>
    );
  };

  return (
    <>
      <div ref={containerRef} className={compact ? 'relative' : 'flex-1 min-w-0 w-[120px] sm:w-[150px]'}>
        {compact ? (
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            title="Ordina"
            className={clsx(
              'w-7 h-7 flex items-center justify-center rounded-md border transition-colors',
              hasPrimary
                ? 'border-accent-violet/50 bg-accent-violet/10 text-accent-violet'
                : 'border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover text-text-tertiary'
            )}
          >
            {icon}
            {hasPrimary && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-accent-violet pointer-events-none" />
            )}
          </button>
        ) : (
        <div className="flex items-center w-full min-h-[36px] rounded-md border border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover transition-colors">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center gap-2 px-2 py-1.5 w-full text-left min-w-0"
          >
            {icon && <span className="text-text-tertiary shrink-0">{icon}</span>}
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="text-[9px] uppercase tracking-wide text-text-quaternary font-semibold leading-none">
                Ordina
              </span>
              <span className="text-[10px] sm:text-[11px] text-text-secondary font-semibold leading-tight truncate whitespace-nowrap">
                {triggerLabel}
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClearAll();
            }}
            className={clsx(
              'w-8 h-[36px] flex items-center justify-center rounded-md hover:bg-surface-hover text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
              hasPrimary ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            title="Rimuovi ordinamento"
            aria-label="Rimuovi ordinamento"
          >
            <X className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="w-8 h-[36px] flex items-center justify-center rounded-md hover:bg-surface-hover text-text-quaternary transition-colors shrink-0"
            aria-label={open ? 'Chiudi ordinamento' : 'Apri ordinamento'}
          >
            <ChevronDown className={clsx('w-3 h-3 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
        )}
      </div>

      {open &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 animate-scale-in origin-top"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            <DropdownShell widthClass="min-w-[260px] max-w-[380px]">
              {/* Search */}
              <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border-primary">
                <Search className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none"
                  placeholder="Cerca campi..."
                  autoFocus
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="p-1 rounded hover:bg-surface-hover text-text-quaternary"
                    title="Cancella ricerca"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              <div className="max-h-[60vh] overflow-y-auto">
                {/* Primary section - always visible */}
                <div className="border-b border-border-primary/60">
                  <div className="flex items-center justify-between px-2.5 py-1">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 shrink-0 flex items-center justify-center">
                        <ChevronRight className="w-3 h-3 text-text-quaternary rotate-90" />
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide leading-none">
                          Ordinamento primario
                        </span>
                        <span className="text-[10px] text-text-quaternary font-medium tabular-nums shrink-0 leading-none">
                          {filteredFields.length}
                        </span>
                        {hasPrimary && (
                          <span className="h-4 flex items-center justify-center px-1.5 rounded text-[9px] font-semibold bg-accent-violet/20 text-accent-violet shrink-0 leading-none">
                            1
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearPrimary}
                      className={clsx(
                        'w-[56px] text-right text-[9px] text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
                        hasPrimary ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      )}
                      aria-label="Cancella ordinamento primario"
                    >
                      Cancella
                    </button>
                  </div>

                  <div className="space-y-0.5 px-1.5 pb-1">
                    {filteredFields.map((opt) => {
                      const isActive = sorting.primary.field === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handlePrimaryClick(opt.value)}
                          className={clsx(
                            'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-surface-hover transition-colors rounded-md min-h-[28px]',
                            isActive && 'bg-surface-hover/80'
                          )}
                        >
                          <span className="w-4 shrink-0 flex items-center justify-center text-text-tertiary">
                            {opt.icon ?? null}
                          </span>
                          <span
                            className={clsx(
                              'flex-1 truncate',
                              isActive ? 'text-accent-violet font-medium' : 'text-text-secondary'
                            )}
                          >
                            {opt.label}
                          </span>
                          {renderDirectionIcon(isActive, sorting.primary.order, handlePrimaryDirection)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Secondary section - only visible when primary is set */}
                {hasPrimary && secondaryFields.length > 0 && (
                  <div className="border-b border-border-primary/60 last:border-b-0">
                    <div className="flex items-center justify-between px-2.5 py-1">
                      <button
                        type="button"
                        onClick={() => setSecondaryExpanded((prev) => !prev)}
                        className="flex items-center min-w-0 flex-1 hover:text-text-primary transition-colors gap-2"
                        title={secondaryExpanded ? 'Comprimi' : 'Espandi'}
                      >
                        <span className="w-3 h-3 shrink-0 flex items-center justify-center">
                          <ChevronRight
                            className={clsx(
                              'w-3 h-3 text-text-quaternary transition-transform origin-center',
                              secondaryExpanded && 'rotate-90'
                            )}
                          />
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide leading-none">
                            Ordinamento secondario
                          </span>
                          <span className="text-[10px] text-text-quaternary font-medium tabular-nums shrink-0 leading-none">
                            {secondaryFields.length}
                          </span>
                          {hasSecondary && (
                            <span className="h-4 flex items-center justify-center px-1.5 rounded text-[9px] font-semibold bg-accent-violet/20 text-accent-violet shrink-0 leading-none">
                              1
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={handleClearSecondary}
                        className={clsx(
                          'w-[56px] text-right text-[9px] text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
                          hasSecondary ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        )}
                        aria-label="Cancella ordinamento secondario"
                      >
                        Cancella
                      </button>
                    </div>

                    {secondaryExpanded && (
                      <div className="space-y-0.5 px-1.5 pb-1">
                        {secondaryFields.map((opt) => {
                          const isActive = sorting.secondary.field === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => handleSecondaryClick(opt.value)}
                              className={clsx(
                                'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-surface-hover transition-colors rounded-md min-h-[28px]',
                                isActive && 'bg-surface-hover/80'
                              )}
                            >
                              <span className="w-4 shrink-0 flex items-center justify-center text-text-tertiary">
                                {opt.icon ?? null}
                              </span>
                              <span
                                className={clsx(
                                  'flex-1 truncate',
                                  isActive ? 'text-accent-violet font-medium' : 'text-text-secondary'
                                )}
                              >
                                {opt.label}
                              </span>
                              {renderDirectionIcon(isActive, sorting.secondary.order, handleSecondaryDirection)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DropdownShell>
          </div>,
          document.body
        )}
    </>
  );
}
